// Copyright ©️ 2026 eVolve MEP, LLC

import {
  formatDate,
  type AssignedCustomProperty,
  type CustomProperty,
  type CustomPropertyDataTypeMap,
} from '@evolvemep/api-ts';
import { InputLabel, Flex, Switch } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { customSetFilterParams } from 'helpers/ag-grid/convertAgGridRequest';
import { CustomSelectComponent } from 'helpers/ag-grid/CustomSelectComponent';
import { CustomValueSetFilterComponent } from 'helpers/ag-grid/CustomSetFilterComponent';
import type { EditorComponent, EditorComponentProps } from 'helpers/ag-grid/EditableCells/EditableCell';
import { editableColDef, type EditableColDef } from 'helpers/ag-grid/EditableCells/editableColDef';

import { NumberInputWrapped } from '../NumberInputWrapped';

export const customPropertyEditor = <P extends CustomProperty>(property: P) => {
  const map = {
    date: DatePickerWrapped,
    dropdown: (props: EditorComponentProps<CustomPropertyDataTypeMap['dropdown']>) => (
      <WrappedSelect
        data={
          property.options?.map((opt) => ({
            value: opt.customPropertyOptionId,
            label: opt.name,
          })) ?? []
        }
        {...props}
      />
    ),
    number: ({ value, onChange, ...props }: EditorComponentProps<CustomPropertyDataTypeMap['number']>) => (
      <NumberInputWrapped
        value={value ?? null}
        onChange={(v) => {
          const val = typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? parseFloat(v) : v;
          if (typeof val === 'number' && !isNaN(val)) onChange(val);
          else if (val === null) onChange(null);
        }}
        maxLength={12}
        {...props}
      />
    ),
    toggle: ({ value, onChange, ...props }: EditorComponentProps<CustomPropertyDataTypeMap['toggle']>) => (
      <Flex justify="space-between" align="center" gap="xs">
        <InputLabel>{property.name}</InputLabel>
        <Switch checked={value ?? false} onChange={(e) => onChange(e.currentTarget.checked || null)} {...props} />
      </Flex>
    ),
    text: ({ value, ...props }: EditorComponentProps<CustomPropertyDataTypeMap['text']>) => (
      <TextareaWrapped
        {...props}
        autosize
        minRows={2}
        maxRows={10}
        maxLength={255}
        value={value ?? ''}
        onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)}
      />
    ),
  } as const satisfies {
    [k in keyof CustomPropertyDataTypeMap]: EditorComponent<CustomPropertyDataTypeMap[k]>;
  };
  return map[property.dataType] as EditorComponent<CustomPropertyDataTypeMap[P['dataType']]>;
};

const getCustomPropertyColDef = <T, P extends CustomProperty>(
  property: P,
): Omit<ColDef<T>, 'field' | 'valueGetter' | 'editable'> &
  Partial<EditableColDef<T, never, CustomPropertyDataTypeMap[P['dataType']]>> => {
  if (property.dataType === 'date') {
    return {
      valueFormatter: ({ value }) => formatDate(value, 'MM/dd/yyyy', '--'),
      closeOnChange: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['inRange'],
        maxNumConditions: 1,
        buttons: ['reset'],
      },
    };
  }
  if (property.dataType === 'dropdown') {
    return {
      closeOnChange: true,
      valueFormatter: ({ value }) => property.options?.find((o) => o.customPropertyOptionId === value)?.name ?? '--',
      filter: CustomValueSetFilterComponent(
        customSetFilterParams(property.options ?? [], 'customPropertyOptionId', 'name'),
      ),
    };
  }
  if (property.dataType === 'number') {
    return {
      /** The base column definition uses '--' for any falsy value, but we want to differentiate zero from null/undefined */
      valueFormatter: ({ value }) => value ?? '--',
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: ['inRange'],
        maxNumConditions: 1,
        buttons: ['reset', 'apply'],
      },
    };
  }
  if (property.dataType === 'toggle') {
    return {
      closeOnChange: true,
      cellRenderer: CellRenderer<T, never, boolean>(({ value }) =>
        value ? <EvolveIcon icon="Check" color="primary" /> : '--',
      ),
      filter: CustomSelectComponent({
        label: property.name,
        defaultValue: '',
        data: [
          { value: '', label: 'All' },
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      }),
    };
  }
  return {
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  };
};

export const getCustomPropertyColDefs = <
  T extends {
    customProperties: AssignedCustomProperty[];
  },
>(
  customProperties: CustomProperty[],
  editable: ((data: T) => boolean) | boolean = true,
): ColDef<T>[] =>
  customProperties.map(
    (prop) =>
      editableColDef({
        colId: `custom_${prop.customPropertyId}`,
        headerName: prop.name,
        headerTooltip: prop.description,
        sortable: true,
        dataMapper: (_, updatedValue) =>
          ({
            customProperties: [
              {
                customPropertyId: prop.customPropertyId,
                dataType: prop.dataType,
                value: updatedValue,
              },
            ],
          }) as Partial<T>,
        Editor: customPropertyEditor(prop),
        clearable: true,
        editable,
        valueGetter: ({ data }) =>
          data?.customProperties.find((c) => c.customPropertyId === prop.customPropertyId)?.value,
        ...getCustomPropertyColDef<T, typeof prop>(prop),
      }) as ColDef<T>,
  );
